import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { Plural, Trans } from "@lingui/react/macro";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Divider,
  FormControl,
  FormControlLabel,
  lighten,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { addDays, addMonths, differenceInDays, format, startOfMonth } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useAccommodationDayInfo } from "@/hooks";
import {
  computeStayLengthAvailability,
  findClosestAvailableNights,
  getValidArrivalDays,
} from "@/utils/alternativeDates";
import { getDateFnsLocale } from "@/utils/booking";

export interface AlternativeDateOptionsProps {
  accoId: number;
  nights: number;
  searchedDateFrom: Date;
  reasonTxt?: string;
  canShowAlternativeDates: boolean;
  onOptionChange: (arrival: Date, departure: Date) => void;
  split?: boolean;
}

interface AlternativeOption {
  arrival: Date;
  departure: Date;
  label: string;
}

export default function AlternativeDateOptions({
  accoId,
  nights,
  searchedDateFrom,
  reasonTxt,
  canShowAlternativeDates,
  onOptionChange,
  split = false,
}: AlternativeDateOptionsProps) {
  const { loading, data: dayInfoData, fetchDayInfo } = useAccommodationDayInfo();
  const { i18n, _ } = useLingui();
  const theme = useTheme();
  const dateFnsLocale = getDateFnsLocale(i18n.locale);
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [adjustedNights, setAdjustedNights] = useState<number | null>(null);

  useEffect(() => {
    const today = new Date();
    const dateFrom = format(today, "yyyy-MM-dd");
    const dateTo = format(addMonths(startOfMonth(today), 12), "yyyy-MM-dd");
    fetchDayInfo({ dateFrom, dateTo, accoId }).catch(() => {});
  }, [accoId, fetchDayInfo]);

  const stayLengthAvailability = useMemo(
    () => (dayInfoData?.day_list ? computeStayLengthAvailability(dayInfoData.day_list) : {}),
    [dayInfoData],
  );

  useEffect(() => {
    if (Object.keys(stayLengthAvailability).length === 0) return;
    setAdjustedNights(findClosestAvailableNights(stayLengthAvailability, nights));
  }, [stayLengthAvailability, nights]);

  const effectiveNights = adjustedNights ?? nights;

  const alternatives = useMemo<AlternativeOption[]>(() => {
    if (!dayInfoData?.day_list) return [];

    const validArrivals = getValidArrivalDays(dayInfoData.day_list, effectiveNights);

    const sorted = validArrivals
      .map((arrival) => ({
        arrival,
        diff: differenceInDays(arrival, searchedDateFrom),
      }))
      .sort((a, b) => Math.abs(a.diff) - Math.abs(b.diff))
      .slice(0, 3);

    return sorted.map(({ arrival, diff }, index) => {
      let label: string;
      if (index === 0) {
        label = _(msg`Closest option`);
      } else if (diff > 0) {
        label = _(msg`${diff} days later`);
      } else {
        label = _(msg`${Math.abs(diff)} days earlier`);
      }
      return { arrival, departure: addDays(arrival, effectiveNights), label };
    });
  }, [dayInfoData, effectiveNights, searchedDateFrom, _]);

  const reasons = reasonTxt
    ? reasonTxt
        .split(/<br\s*\/?>/i)
        .map((r) => r.trim().replace(/,+$/, ""))
        .filter(Boolean)
    : [];

  const paperSx = split
    ? {
        borderWidth: 1,
        borderColor: "divider",
        borderStyle: "solid",
        borderRadius: 3,
        overflow: "hidden",
      }
    : {
        borderWidth: { xs: 0, md: 1 },
        borderColor: "divider",
        borderStyle: "solid",
        borderRadius: { xs: 0, md: 3 },
        overflow: "hidden",
      };

  const alerts: React.ReactNode[] = [
    ...reasons,
    ...(canShowAlternativeDates && adjustedNights !== null
      ? [
          <Trans key="adjusted-nights">
            No <Plural value={nights} one="# night" other="# nights" /> stay is available. Showing{" "}
            <Plural value={adjustedNights} one="# night" other="# nights" /> instead — the closest
            available option.
          </Trans>,
        ]
      : []),
  ];

  const alertsSection = alerts.length > 0 && (
    <Stack sx={{ bgcolor: theme.palette.warning.light }} spacing={0.5} p={1.5}>
      {alerts.map((alert, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: alerts are position-stable within a render
        <Stack key={i} direction="row" alignItems="top" spacing={0.75}>
          <InfoOutlinedIcon
            sx={{
              fontSize: 18,
              color: theme.palette.warning.contrastText,
              flexShrink: 0,
              position: "relative",
              top: "2px",
            }}
          />
          <Typography variant="body1" color={theme.palette.warning.contrastText}>
            {alert}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );

  const alternativesSection = canShowAlternativeDates && (loading || alternatives.length > 0) && (
    <>
      {loading && (
        <Stack spacing={0.5} p={1.5}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="text" width="100%" />
          ))}
        </Stack>
      )}
      {!loading && alternatives.length > 0 && (
        <FormControl
          component="fieldset"
          sx={{ width: "100%", px: 1.5, py: 1, bgcolor: lighten(theme.palette.primary.main, 0.85) }}
          onClick={(e) => e.stopPropagation()}
        >
          <RadioGroup
            value={selectedValue}
            onChange={(e) => {
              const value = e.target.value;
              setSelectedValue(value);
              const option = alternatives.find((o) => format(o.arrival, "yyyy-MM-dd") === value);
              if (option) onOptionChange(option.arrival, option.departure);
            }}
          >
            {alternatives.map((option) => {
              const selected = selectedValue === format(option.arrival, "yyyy-MM-dd");
              const value = format(option.arrival, "yyyy-MM-dd");
              const dateLabel =
                option.arrival.getMonth() === option.departure.getMonth()
                  ? `${format(option.arrival, "MMM d", { locale: dateFnsLocale })}–${format(option.departure, "d", { locale: dateFnsLocale })}`
                  : `${format(option.arrival, "MMM d", { locale: dateFnsLocale })} – ${format(option.departure, "MMM d", { locale: dateFnsLocale })}`;
              return (
                <FormControlLabel
                  key={value}
                  value={value}
                  labelPlacement="start"
                  control={<Radio size="small" />}
                  sx={{ width: "100%", justifyContent: "space-between", ml: 0 }}
                  label={
                    <Stack direction={{ xs: "row", md: "column" }} spacing={{ xs: 1, md: 0 }}>
                      <Typography variant="body1" fontWeight={selected ? "bold" : "regular"}>
                        {dateLabel}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        fontWeight={selected ? "bold" : "regular"}
                        display={{ xs: "none", md: "block" }}
                      >
                        {option.label}
                      </Typography>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        fontWeight={selected ? "bold" : "regular"}
                        display={{ xs: "block", md: "none" }}
                      >
                        ({option.label})
                      </Typography>
                    </Stack>
                  }
                />
              );
            })}
          </RadioGroup>
        </FormControl>
      )}
    </>
  );

  if (split) {
    return (
      <Stack spacing={1}>
        {alertsSection && <Paper sx={paperSx}>{alertsSection}</Paper>}
        {alternativesSection && <Paper sx={paperSx}>{alternativesSection}</Paper>}
      </Stack>
    );
  }

  return (
    <Paper sx={paperSx}>
      <Divider />
      {alertsSection}
      {(loading || alternatives.length > 0) && <Divider />}
      {alternativesSection}
      <Divider />
    </Paper>
  );
}
